<template>
  <div class="modal fade plot-billing-modal" data-bs-backdrop="static" data-bs-keyboard="false" tabindex="-1" role="dialog" aria-labelledby="plotBillingModalLabel" aria-hidden="true">
    <div class="modal-dialog modal-lg modal-dialog-centered" role="document">
      <div class="modal-content">
        <div class="modal-header bg-success text-white">
          <h5 class="modal-title fw-bold text-white" id="plotBillingModalLabel">
            <i class="mdi mdi-receipt me-2"></i>Create Bill - Plot Rent
          </h5>
          <button type="button" class="btn-close btn-close-white" data-bs-dismiss="modal" aria-label="Close"></button>
        </div>

        <div class="modal-body p-0" v-if="plot">
          <!-- Plot Information Card -->
          <div class="bg-light border-bottom p-4">
            <div class="row">
              <div class="col-md-6">
                <div class="property-info">
                  <h6 class="fw-bold text-success mb-3">
                    <i class="mdi mdi-store me-2"></i>Plot Details
                  </h6>
                  <div class="info-row mb-2">
                    <span class="text-muted">Plot Number:</span>
                    <span class="fw-bold ms-2">{{ plot.plotNumber || 'N/A' }}</span>
                  </div>
                  <div class="info-row mb-2">
                    <span class="text-muted">Zone/Market:</span>
                    <span class="fw-bold ms-2">{{ plot.zone || 'N/A' }}</span>
                  </div>
                  <div class="info-row mb-2">
                    <span class="text-muted">Owner:</span>
                    <span class="fw-bold ms-2">{{ plot.ownerNames || 'N/A' }}</span>
                  </div>
                  <div class="info-row">
                    <span class="text-muted">Location:</span>
                    <span class="fw-bold ms-2">{{ plot.subCountyName || '' }} {{ plot.wardName ? '- ' + plot.wardName : '' }}</span>
                  </div>
                </div>
              </div>

              <div class="col-md-6">
                <div class="financial-info">
                  <h6 class="fw-bold text-success mb-3">
                    <i class="mdi mdi-currency-usd me-2"></i>Financial Summary
                  </h6>
                  <div class="info-row mb-2">
                    <span class="text-muted">Annual Rent:</span>
                    <span class="fw-bold text-success ms-2">KES {{ numberFormat(plot.annualRent) }}</span>
                  </div>
                  <div class="info-row mb-2">
                    <span class="text-muted">Arrears B/F:</span>
                    <span class="fw-bold text-danger ms-2">KES {{ numberFormat(plot.arrearsForward) }}</span>
                  </div>
                  <div class="info-row mb-2">
                    <span class="text-muted">Current Balance:</span>
                    <span class="fw-bold ms-2" :class="balanceClass">KES {{ numberFormat(plot.currentBalance) }}</span>
                  </div>
                  <div class="info-row">
                    <span class="text-muted">Status:</span>
                    <span class="ms-2">
                      <span class="badge" :class="statusBadgeClass">{{ plot.status || 'Active' }}</span>
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Billing Form -->
          <div class="p-4">
            <div class="text-center mb-4">
              <div class="billing-icon mb-3">
                <i class="mdi mdi-receipt display-1 text-success opacity-25"></i>
              </div>
              <h5 class="fw-bold text-dark mb-2">Generate Invoice</h5>
              <p class="text-muted mb-0">Select billing details and enter the amount to invoice</p>
            </div>

            <form @submit.prevent="handleGenerateBill">
              <div class="row">
                <!-- Billing Type Selection - NEW -->
                <div class="col-12 mb-3">
                  <label class="form-label fw-semibold">
                    Billing Type <span class="text-danger">*</span>
                  </label>
                  <div class="d-flex gap-3">
                    <div class="form-check form-check-inline">
                      <input
                          v-model="billingType"
                          class="form-check-input"
                          type="radio"
                          name="billingType"
                          id="billingTypeExisting"
                          value="existing">
                      <label class="form-check-label" for="billingTypeExisting">
                        <i class="mdi mdi-file-document-outline text-info me-1"></i>
                        Invoice for Existing Balance
                        <small class="d-block text-muted">Generate invoice for outstanding amount (no new charges)</small>
                      </label>
                    </div>
                    <div class="form-check form-check-inline">
                      <input
                          v-model="billingType"
                          class="form-check-input"
                          type="radio"
                          name="billingType"
                          id="billingTypeNew"
                          value="new">
                      <label class="form-check-label" for="billingTypeNew">
                        <i class="mdi mdi-plus-circle text-success me-1"></i>
                        Add New Charges
                        <small class="d-block text-muted">Bill for new annual rent or additional charges</small>
                      </label>
                    </div>
                  </div>
                </div>

                <!-- Income Type Selection -->
                <div class="col-md-6">
                  <div class="form-group mb-3">
                    <label class="form-label fw-semibold">
                      Income Type <span class="text-danger">*</span>
                    </label>
                    <select
                        v-model="selectedIncomeType"
                        @change="handleIncomeTypeChange"
                        class="form-select"
                        :class="{ 'is-invalid': errors.incomeType }"
                        required>
                      <option value="">Select Income Type</option>
                      <option v-for="(item, index) in incomeTypes" :key="index" :value="item">
                        {{ item.incomeTypeDescription }}
                      </option>
                    </select>
                    <div v-if="errors.incomeType" class="invalid-feedback">{{ errors.incomeType }}</div>
                  </div>
                </div>

                <!-- Fee Type Selection -->
                <div class="col-md-6">
                  <div class="form-group mb-3">
                    <label class="form-label fw-semibold">
                      Fee Type <span class="text-danger">*</span>
                    </label>
                    <select
                        v-model="selectedFeeType"
                        @change="handleFeeTypeChange"
                        class="form-select"
                        :class="{ 'is-invalid': errors.feeType }"
                        :disabled="!selectedIncomeType || loadingFees"
                        required>
                      <option value="">{{ loadingFees ? 'Loading...' : 'Select Fee Type' }}</option>
                      <option v-for="(item, index) in feesAndCharges" :key="index" :value="item">
                        {{ item.feeDescription }} - KES {{ numberFormat(item.unitFeeAmount || 0) }}
                      </option>
                    </select>
                    <div v-if="errors.feeType" class="invalid-feedback">{{ errors.feeType }}</div>
                    <small v-if="!selectedIncomeType" class="text-muted">Select income type first</small>
                  </div>
                </div>

                <!-- Fiscal Year -->
                <div class="col-md-6">
                  <div class="form-group mb-3">
                    <label class="form-label fw-semibold">
                      Fiscal Year <span class="text-danger">*</span>
                    </label>
                    <select v-model="fiscalYear" class="form-select" required>
                      <option :value="currentYear - 1">{{ currentYear - 1 }}</option>
                      <option :value="currentYear">{{ currentYear }}</option>
                      <option :value="currentYear + 1">{{ currentYear + 1 }}</option>
                    </select>
                  </div>
                </div>

                <!-- Amount Input -->
                <div class="col-md-6">
                  <div class="form-group mb-3">
                    <label class="form-label fw-semibold">
                      Amount to Invoice (KES) <span class="text-danger">*</span>
                    </label>
                    <div class="input-group">
                      <span class="input-group-text">KES</span>
                      <input
                          v-model.number="invoiceAmount"
                          type="number"
                          step="0.01"
                          min="0"
                          class="form-control"
                          :class="{ 'is-invalid': errors.amount }"
                          placeholder="Enter amount"
                          required>
                    </div>
                    <div v-if="errors.amount" class="invalid-feedback d-block">{{ errors.amount }}</div>
                  </div>
                </div>

                <!-- Quick Amount Buttons -->
                <div class="col-12 mb-3">
                  <small class="text-muted me-2">Quick amounts:</small>

                  <!-- For Existing Balance billing -->
                  <template v-if="billingType === 'existing'">
                    <button
                        v-if="plot.currentBalance > 0"
                        type="button"
                        class="btn btn-outline-danger btn-sm me-2"
                        @click="setQuickAmount(plot.currentBalance)">
                      Full Balance ({{ numberFormat(plot.currentBalance) }})
                    </button>
                    <button
                        v-if="plot.arrearsForward > 0 && plot.arrearsForward !== plot.currentBalance"
                        type="button"
                        class="btn btn-outline-warning btn-sm me-2"
                        @click="setQuickAmount(plot.arrearsForward)">
                      Arrears B/F ({{ numberFormat(plot.arrearsForward) }})
                    </button>
                  </template>

                  <!-- For New Charges billing -->
                  <template v-if="billingType === 'new'">
                    <button
                        v-if="plot.annualRent > 0"
                        type="button"
                        class="btn btn-outline-success btn-sm me-2"
                        @click="setQuickAmount(plot.annualRent)">
                      Annual Rent ({{ numberFormat(plot.annualRent) }})
                    </button>
                    <button
                        v-if="selectedFeeType && selectedFeeType.unitFeeAmount > 1"
                        type="button"
                        class="btn btn-outline-primary btn-sm"
                        @click="setQuickAmount(selectedFeeType.unitFeeAmount)">
                      Fee Amount ({{ numberFormat(selectedFeeType.unitFeeAmount) }})
                    </button>
                  </template>
                </div>

                <!-- Billing Summary -->
                <div class="col-12">
                  <div class="card border-0 mb-4" :class="billingType === 'existing' ? 'bg-light' : 'bg-warning bg-opacity-10'" v-if="invoiceAmount > 0">
                    <div class="card-body">
                      <h6 class="fw-bold mb-3" :class="billingType === 'existing' ? 'text-info' : 'text-success'">
                        <i class="mdi me-2" :class="billingType === 'existing' ? 'mdi-file-document-outline' : 'mdi-calculator'"></i>
                        {{ billingType === 'existing' ? 'Invoice Summary (Existing Balance)' : 'Billing Summary (New Charges)' }}
                      </h6>
                      <div class="row">
                        <div class="col-md-6">
                          <div class="summary-item mb-2">
                            <span class="text-muted">Plot:</span>
                            <span class="fw-bold ms-2">{{ plot.plotNumber }} - {{ plot.zone }}</span>
                          </div>
                          <div class="summary-item mb-2">
                            <span class="text-muted">Fee Type:</span>
                            <span class="fw-bold ms-2">{{ selectedFeeType?.feeDescription || 'Not selected' }}</span>
                          </div>
                          <div class="summary-item mb-2">
                            <span class="text-muted">Fiscal Year:</span>
                            <span class="fw-bold ms-2">{{ fiscalYear }}</span>
                          </div>
                        </div>
                        <div class="col-md-6">
                          <div class="summary-item mb-2">
                            <span class="text-muted">Current Balance:</span>
                            <span class="fw-bold text-danger ms-2">KES {{ numberFormat(plot.currentBalance) }}</span>
                          </div>
                          <div class="summary-item mb-2">
                            <span class="text-muted">Invoice Amount:</span>
                            <span class="fw-bold text-primary ms-2 h6">KES {{ numberFormat(invoiceAmount) }}</span>
                          </div>

                          <!-- Show different info based on billing type -->
                          <template v-if="billingType === 'existing'">
                            <div class="summary-item">
                              <span class="text-muted">Balance After Invoice:</span>
                              <span class="fw-bold ms-2 text-danger">
                                KES {{ numberFormat(plot.currentBalance) }}
                                <small class="text-muted">(unchanged)</small>
                              </span>
                            </div>
                          </template>
                          <template v-else>
                            <div class="summary-item">
                              <span class="text-muted">New Balance:</span>
                              <span class="fw-bold ms-2" :class="newBalanceClass">
                                KES {{ numberFormat(newBalance) }}
                              </span>
                            </div>
                          </template>
                        </div>
                      </div>

                      <!-- Info Alert based on billing type -->
                      <div class="alert mt-3 mb-0" :class="billingType === 'existing' ? 'alert-info' : 'alert-warning'">
                        <small>
                          <i class="mdi mdi-information-outline me-1"></i>
                          <template v-if="billingType === 'existing'">
                            <strong>Invoice for Existing Balance:</strong> This creates an invoice document for the outstanding amount.
                            The balance will remain at <strong>KES {{ numberFormat(plot.currentBalance) }}</strong> until payment is received.
                          </template>
                          <template v-else>
                            <strong>New Charges:</strong> This will add <strong>KES {{ numberFormat(invoiceAmount) }}</strong> to the account.
                            The new balance will be <strong>KES {{ numberFormat(newBalance) }}</strong>.
                          </template>
                        </small>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Action Buttons -->
              <div class="d-flex gap-2">
                <button type="button" class="btn btn-outline-secondary flex-fill" data-bs-dismiss="modal">
                  <i class="mdi mdi-close me-1"></i>Cancel
                </button>
                <button
                    type="submit"
                    class="btn btn-success flex-fill"
                    :disabled="!isFormValid || isGenerating">
                  <span v-if="isGenerating">
                    <span class="spinner-border spinner-border-sm me-2" role="status"></span>
                    Generating...
                  </span>
                  <span v-else>
                    <i class="mdi mdi-receipt me-1"></i>Generate Invoice
                  </span>
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
/* eslint-disable */
export default {
  name: 'PlotBillingModal',
  props: {
    plot: {
      type: Object,
      default: () => ({})
    },
    incomeTypes: {
      type: Array,
      default: () => []
    },
    feesAndCharges: {
      type: Array,
      default: () => []
    },
    loadingFees: {
      type: Boolean,
      default: false
    }
  },
  emits: ['generate-bill', 'income-type-changed'],
  data() {
    return {
      invoiceAmount: 0,
      selectedIncomeType: null,
      selectedFeeType: null,
      fiscalYear: new Date().getFullYear(),
      currentYear: new Date().getFullYear(),
      isGenerating: false,
      errors: {},
      hasAutoSelectedIncomeType: false,
      hasAutoSelectedFeeType: false,
      billingType: 'existing' // NEW: 'existing' for existing balance, 'new' for new charges
    }
  },
  computed: {
    balanceClass() {
      if (!this.plot) return 'text-muted';
      const balance = parseFloat(this.plot.currentBalance) || 0;
      return balance > 0 ? 'text-danger' : 'text-success';
    },

    statusBadgeClass() {
      if (!this.plot) return 'bg-secondary';
      const status = this.plot.status || 'Active';
      switch (status) {
        case 'Active': return 'bg-success';
        case 'Suspended': return 'bg-danger';
        case 'Vacant': return 'bg-secondary';
        case 'Transferred': return 'bg-info';
        default: return 'bg-secondary';
      }
    },

    newBalance() {
      if (!this.plot) return 0;

      // For existing balance billing, balance doesn't change
      if (this.billingType === 'existing') {
        return parseFloat(this.plot.currentBalance) || 0;
      }

      // For new charges, add to current balance
      return (parseFloat(this.plot.currentBalance) || 0) + this.invoiceAmount;
    },

    newBalanceClass() {
      return this.newBalance > 0 ? 'text-danger' : 'text-success';
    },

    isFormValid() {
      return this.plot &&
          this.selectedIncomeType &&
          this.selectedFeeType &&
          this.invoiceAmount > 0 &&
          this.fiscalYear &&
          this.billingType &&
          Object.keys(this.errors).length === 0;
    }
  },
  watch: {
    plot: {
      handler(newVal) {
        // Reset auto-selection flags when plot changes
        this.hasAutoSelectedIncomeType = false;
        this.hasAutoSelectedFeeType = false;

        // Reset billing type to existing (most common case)
        this.billingType = 'existing';

        // Auto-set amount based on billing type
        if (newVal && this.invoiceAmount === 0) {
          this.setDefaultAmount();
        }
      },
      immediate: true,
      deep: true
    },

    // When billing type changes, update the default amount
    billingType(newVal) {
      this.setDefaultAmount();
    },

    invoiceAmount(newVal) {
      this.validateAmount(newVal);
    },

    incomeTypes: {
      handler(newVal) {
        if (newVal && newVal.length > 0 && !this.hasAutoSelectedIncomeType) {
          // Find "Plot Rent" income type (exact match first, then partial match)
          let plotRentType = newVal.find(item =>
              item.incomeTypeDescription === 'Plot Rent'
          );

          // If exact match not found, try partial match
          if (!plotRentType) {
            plotRentType = newVal.find(item =>
                item.incomeTypeDescription &&
                item.incomeTypeDescription.toLowerCase().includes('plot rent') &&
                !item.incomeTypeDescription.toLowerCase().includes('other')
            );
          }

          // If still not found, use first option
          if (!plotRentType && newVal.length === 1) {
            plotRentType = newVal[0];
          }

          if (plotRentType) {
            this.selectedIncomeType = plotRentType;
            this.hasAutoSelectedIncomeType = true;
            this.handleIncomeTypeChange();
          }
        }
      },
      immediate: true
    },

    feesAndCharges: {
      handler(newVal) {
        if (newVal && newVal.length > 0 && !this.hasAutoSelectedFeeType) {
          // Find "Plot Rent Fee" (exact match first, then partial match)
          let plotRentFee = newVal.find(item =>
              item.feeDescription === 'Plot Rent Fee'
          );

          // If exact match not found, try partial match
          if (!plotRentFee) {
            plotRentFee = newVal.find(item =>
                item.feeDescription &&
                item.feeDescription.toLowerCase().includes('plot rent')
            );
          }

          // If still not found and only one option, use it
          if (!plotRentFee && newVal.length === 1) {
            plotRentFee = newVal[0];
          }

          if (plotRentFee) {
            this.selectedFeeType = plotRentFee;
            this.hasAutoSelectedFeeType = true;
          }
        }
      },
      immediate: true
    }
  },
  methods: {
    setDefaultAmount() {
      if (!this.plot) return;

      if (this.billingType === 'existing') {
        // For existing balance, default to current balance
        const currentBalance = parseFloat(this.plot.currentBalance) || 0;
        if (currentBalance > 0) {
          this.invoiceAmount = currentBalance;
        } else {
          this.invoiceAmount = 0;
        }
      } else {
        // For new charges, default to annual rent
        const annualRent = parseFloat(this.plot.annualRent) || 0;
        this.invoiceAmount = annualRent;
      }
    },

    handleIncomeTypeChange() {
      this.selectedFeeType = null; // Reset fee type when income type changes
      this.hasAutoSelectedFeeType = false; // Allow auto-selection of fee type
      if (this.selectedIncomeType) {
        this.$emit('income-type-changed', this.selectedIncomeType);
      }
    },

    handleFeeTypeChange() {
      // For new charges, auto-set amount to fee unit amount if not already set
      if (this.billingType === 'new' && this.selectedFeeType?.unitFeeAmount > 1 && this.invoiceAmount === 0) {
        this.invoiceAmount = parseFloat(this.selectedFeeType.unitFeeAmount);
      }
    },

    setQuickAmount(amount) {
      this.invoiceAmount = parseFloat(amount) || 0;
    },

    validateAmount(amount) {
      delete this.errors.amount;

      if (!amount || amount <= 0) {
        this.errors.amount = 'Amount must be greater than 0';
      } else if (amount > 10000000) {
        this.errors.amount = 'Amount seems too large. Please verify.';
      }

      // For existing balance billing, warn if amount exceeds current balance
      if (this.billingType === 'existing' && amount > (parseFloat(this.plot?.currentBalance) || 0)) {
        // Just a warning, not an error
        console.warn('Invoice amount exceeds current balance for existing balance billing');
      }
    },

    validateForm() {
      this.errors = {};

      if (!this.selectedIncomeType) {
        this.errors.incomeType = 'Please select an income type';
      }

      if (!this.selectedFeeType) {
        this.errors.feeType = 'Please select a fee type';
      }

      this.validateAmount(this.invoiceAmount);

      return Object.keys(this.errors).length === 0;
    },

    handleGenerateBill() {
      if (!this.validateForm()) {
        return;
      }

      this.isGenerating = true;

      // Emit the bill generation event with all necessary data for generateBill3
      const billingData = {
        plotID: this.plot.id,
        plotNumber: this.plot.plotNumber,
        zone: this.plot.zone,
        ownerNames: this.plot.ownerNames,
        ownerID: this.plot.ownerID,
        ownerPhoneNo: this.plot.ownerPhoneNo,
        ownerEmail: this.plot.ownerEmail || '',
        subCountyID: this.plot.subCountyID,
        subCountyName: this.plot.subCountyName,
        wardID: this.plot.wardID,
        wardName: this.plot.wardName,
        incomeTypeId: this.selectedIncomeType.incomeTypeId,
        incomeTypeDescription: this.selectedIncomeType.incomeTypeDescription,
        incomeTypePrefix: this.selectedIncomeType.incomeTypePrefix || 'PLTRENT',
        feeId: this.selectedFeeType.feeId,
        feeDescription: this.selectedFeeType.feeDescription,
        accountNo: this.selectedFeeType.accountNo || '',
        costCenterNo: this.selectedFeeType.costCenterNo || '',
        fiscalYear: this.fiscalYear,
        amount: this.invoiceAmount,
        balanceBefore: parseFloat(this.plot.currentBalance) || 0,
        annualRent: parseFloat(this.plot.annualRent) || 0,
        arrearsForward: parseFloat(this.plot.arrearsForward) || 0,
        billingType: this.billingType // NEW: Include billing type for backend processing
      };

      this.$emit('generate-bill', billingData);
    },

    numberFormat(num) {
      return new Intl.NumberFormat("en-GB").format(num || 0);
    },

    resetForm() {
      this.invoiceAmount = 0;
      this.selectedIncomeType = null;
      this.selectedFeeType = null;
      this.fiscalYear = new Date().getFullYear();
      this.errors = {};
      this.isGenerating = false;
      this.hasAutoSelectedIncomeType = false;
      this.hasAutoSelectedFeeType = false;
      this.billingType = 'existing';
    },

    stopGenerating() {
      this.isGenerating = false;
    }
  }
}
</script>

<style scoped>
.info-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.summary-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.billing-icon {
  position: relative;
}

.property-info,
.financial-info {
  background: white;
  padding: 1rem;
  border-radius: 0.5rem;
  border: 1px solid #e9ecef;
  height: 100%;
}

.form-group {
  position: relative;
}

.input-group-text {
  background-color: #f8f9fa;
  border-right: none;
  font-weight: 600;
}

.form-control:focus {
  border-color: #28a745;
  box-shadow: 0 0 0 0.2rem rgba(40, 167, 69, 0.25);
}

.form-select:focus {
  border-color: #28a745;
  box-shadow: 0 0 0 0.2rem rgba(40, 167, 69, 0.25);
}

.btn-outline-danger:hover,
.btn-outline-success:hover,
.btn-outline-warning:hover,
.btn-outline-primary:hover {
  transform: translateY(-1px);
}

.form-check-inline {
  flex: 1;
  padding: 1rem;
  border: 1px solid #dee2e6;
  border-radius: 0.5rem;
  margin-right: 0;
  transition: all 0.2s ease;
}

.form-check-inline:hover {
  border-color: #28a745;
  background-color: #f8f9fa;
}

.form-check-inline:has(input:checked) {
  border-color: #28a745;
  background-color: rgba(40, 167, 69, 0.1);
}

.form-check-label {
  cursor: pointer;
  width: 100%;
}

@media (max-width: 768px) {
  .info-row,
  .summary-item {
    flex-direction: column;
    align-items: flex-start;
  }

  .info-row .fw-bold,
  .summary-item .fw-bold {
    margin-left: 0 !important;
    margin-top: 0.25rem;
  }

  .d-flex.gap-3 {
    flex-direction: column;
  }

  .form-check-inline {
    margin-bottom: 0.5rem;
  }
}
</style>